import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PLANS } from '../lib/plans.js';
import { FAQS } from '../content/faqs.js';

/**
 * Public /pricing page (SEO audit MK-4). The Landing page keeps its
 * #pricing section for scroll-through visitors; this dedicated route
 * exists because "markaii pricing" and category-pricing queries are the
 * highest-intent searches a SaaS gets, and they deserve a canonical,
 * indexable URL with its own title/description (seoMeta registry).
 * Reuses the same PLANS source of truth and billing-* card classes as
 * Landing/Billing so prices can never drift.
 */
export default function Pricing() {
  const [cadence, setCadence] = useState('monthly');

  return (
    <div className="landing-shell">
      <header className="landing-nav">
        <div className="landing-nav-inner">
          <Link to="/" className="landing-brand">
            <img src="/icons/markaii-m.png?v=2" alt="" className="brand-icon" />
            <span>Markaii</span>
          </Link>
          <nav className="landing-nav-links">
            <Link to="/">Home</Link>
            <Link to="/login" className="landing-nav-signin">Sign in</Link>
            <Link to="/signup" className="btn btn-primary btn-compact">Start free trial</Link>
          </nav>
        </div>
      </header>

      <section className="landing-section">
        <div className="landing-container">
          <h1 className="landing-h2">Simple pricing for every stage</h1>
          <p className="landing-pricing-sub">
            Markaii replaces a full-time marketer — priced to reflect that.
            One subscription covers all brands on your plan. Every plan
            starts with a 14-day free trial.
          </p>

          <div className="billing-cadence-toggle landing-cadence-toggle" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={cadence === 'monthly'}
              className={`billing-cadence-btn ${cadence === 'monthly' ? 'active' : ''}`}
              onClick={() => setCadence('monthly')}
            >
              Monthly
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={cadence === 'annual'}
              className={`billing-cadence-btn ${cadence === 'annual' ? 'active' : ''}`}
              onClick={() => setCadence('annual')}
            >
              Annual <span className="billing-cadence-badge">−17%</span>
            </button>
          </div>

          <div className="billing-plans landing-plans">
            {PLANS.map((plan) => {
              const display = plan[cadence];
              return (
                <div
                  key={plan.tier}
                  className={`billing-plan-card ${plan.recommended ? 'recommended' : ''}`}
                >
                  {plan.recommended && (
                    <span className="billing-plan-badge">Most popular</span>
                  )}
                  <h4>{plan.name}</h4>
                  <div className="plan-price">
                    {display.price}{' '}
                    <span className="plan-price-sub">{display.sub}</span>
                  </div>
                  {cadence === 'annual' && display.save && (
                    <div className="plan-price-save">{display.save}</div>
                  )}
                  <p className="plan-blurb">{plan.blurb}</p>
                  <ul>
                    {plan.features.map((f) => <li key={f}>{f}</li>)}
                  </ul>
                  <Link
                    to={`/signup?plan=${plan.tier}&cadence=${cadence}`}
                    className={`btn ${plan.recommended ? 'btn-primary' : ''} btn-block`}
                  >
                    Start free trial
                  </Link>
                </div>
              );
            })}
          </div>

          <p className="landing-pricing-footnote">
            Need more than 10 brands?{' '}
            <a href="mailto:sales@markaii.com?subject=Agency+scale+inquiry">Contact sales</a>
            {' '}— Agency extends to 15 brands with a custom contract. Above 15 we set up
            enterprise pricing.
          </p>
        </div>
      </section>

      <section className="landing-section landing-faq-section">
        <div className="landing-container">
          <h2 className="landing-h2">Pricing questions</h2>
          <div className="landing-faq-list">
            {FAQS.map((f) => (
              <details key={f.q} className="landing-faq-item">
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="landing-container landing-footer-inner">
          <span className="landing-brand-mini">Markaii</span>
          <span className="landing-footer-meta">
            <Link to="/privacy">Privacy</Link>
            {' · '}
            <Link to="/terms">Terms</Link>
            {' · '}
            <a href="/llms.txt">For AI agents</a>
          </span>
        </div>
      </footer>
    </div>
  );
}
